import { Empty, Flex, Skeleton } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"

import type { RootState } from "../../../../store.ts"
import {
  CONTRACT_STATUSES,
  CONTRACT_STATUSES_OBJ
} from "../../constants/contract-statuses-keys.ts"
import { contractsSetFilters } from "../../redux/contracts.slice.ts"
import {
  fetchContracts,
  getContractFilterOptions
} from "../../redux/contracts.thunks.ts"
import { type ContractFilters } from "../../redux/contracts.types.ts"
import { ContractsSidebarHeader } from "./ContractsSidebarHeader.tsx"
import { ContractsSidebarList } from "./ContractsSidebarList.tsx"

function ContractsSidebar({
  result,
  filters,
  filterOptions,
  fetching,
  viewLeadAllowed,
  getContracts,
  setFilters,
  getFilterOptions
}: ConnectedProps<typeof connector>) {
  const statuses = useMemo(() => {
    const statusMapping: Record<string, string[]> = {
      [CONTRACT_STATUSES.PENDING_TO_REVIEW]: [
        CONTRACT_STATUSES.PENDING_TO_REVIEW
      ],
      [CONTRACT_STATUSES.APPROVED]: [
        CONTRACT_STATUSES.APPROVED,
        CONTRACT_STATUSES.SENT_TO_CLIENT
      ],
      [CONTRACT_STATUSES.CANCELLED]: [CONTRACT_STATUSES.CANCELLED],
      [CONTRACT_STATUSES.DECLINED]: [CONTRACT_STATUSES.DECLINED]
    }

    const out: BaseOptionType[] = []
    for (const s of filterOptions?.status || []) {
      if (!(s?.value in statusMapping)) continue
      out.push({
        ...s,
        value: statusMapping[s.value]!.join(","),
        label: CONTRACT_STATUSES_OBJ[s.value]?.label ?? s.label
      })
    }
    return out
  }, [filterOptions?.status])

  const handleLoadMoreContract = () => {
    if (fetching) return
  }

  const handleFilterChange = (
    key: keyof ContractFilters,
    value: string | null
  ) => {
    const next = { ...filters, [key]: value }
    setFilters({ [key]: value })
    getContracts(next)
  }

  useEffect(() => {
    getContracts(filters)
    getFilterOptions()
  }, [])

  return (
    <Flex
      vertical
      className={"max-w-[410px] border-r w-full shrink-0 border-border"}
    >
      <ContractsSidebarHeader
        onFilterChange={handleFilterChange}
        statuses={statuses}
        filters={filters}
      />

      {fetching ? (
        <Skeleton.Node className={"size-full"} active />
      ) : (
        <>
          {result?.results?.length ? (
            <ContractsSidebarList
              hasMore={result?.count > result?.results?.length}
              data={result?.results || []}
              viewLeadAllowed={viewLeadAllowed}
              onLoadMoreContracts={handleLoadMoreContract}
            />
          ) : (
            <Empty
              className={"my-4"}
              description={
                filters?.q ? "No contracts found" : "No contracts yet"
              }
            />
          )}
        </>
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.contracts.fetchingList,
  filters: state.contracts.filters,
  filterOptions: state.contracts.filterOptions,
  result: state.contracts.result,
  viewLeadAllowed:
    state?.auth?.userInfo?.permissions?.codes?.leads?.leads?.lead_view
})

const mapDispatchToProps = {
  getContracts: fetchContracts,
  setFilters: contractsSetFilters,
  getFilterOptions: getContractFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ContractsSidebar)
